import { PROMO_CAMPAIGN_STATUS, PROMO_CONFIG } from './promoConfig';
import { isCooldownExpired } from './promoStorage';

function parseCampaignInstant(value, boundary) {
  if (value == null) {
    return null;
  }

  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const isDateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  if (isDateOnly) {
    if (boundary === 'start') {
      date.setHours(0, 0, 0, 0);
    } else {
      date.setHours(23, 59, 59, 999);
    }
  }

  return date.getTime();
}

function isCampaignDateEligible(now, campaign, previewMode) {
  if (previewMode) {
    return true;
  }

  const startAt = parseCampaignInstant(campaign.startDate, 'start');
  if (startAt != null && now < startAt) {
    return false;
  }

  const endAt = parseCampaignInstant(campaign.endDate, 'end');
  if (endAt != null && now > endAt) {
    return false;
  }

  return true;
}

function isUserEligible(isAuthenticated, eligibility) {
  if (isAuthenticated) {
    return eligibility.authenticated !== false;
  }

  return eligibility.guest !== false;
}

function isRouteEligible(pathname, allowedRoutes, allowedRoutePrefixes) {
  if (allowedRoutePrefixes?.some((prefix) => pathname.startsWith(prefix))) {
    return true;
  }

  if (!allowedRoutes?.length) {
    return true;
  }

  return allowedRoutes.includes(pathname);
}

/**
 * Central promotion lifecycle decision — reusable by all promotional surfaces.
 *
 * Check order:
 * 1. Master feature flag
 * 2. Surface feature flag
 * 3. Campaign status (skipped in preview mode)
 * 4. Campaign dates (skipped in preview mode)
 * 5. Route
 * 6. User eligibility
 * 7. Cooldown — homepage popup is once per tab for 24h (always enforced)
 *
 * @param {string} surface - One of PROMO_SURFACES values.
 * @param {object} [options]
 * @param {string} [options.pathname]
 * @param {boolean} [options.isAuthenticated=false]
 * @param {number} [options.now=Date.now()]
 * @returns {boolean}
 */
export function canShowPromotion(surface, options = {}) {
  const { pathname = '', isAuthenticated = false, now = Date.now() } = options;
  const { flags, campaign, surfaces } = PROMO_CONFIG;

  if (!flags.enabled) {
    return false;
  }

  if (!flags[surface]) {
    return false;
  }

  const previewMode = flags.previewMode === true;
  const surfaceConfig = surfaces[surface] ?? {};

  if (!previewMode && campaign.status !== PROMO_CAMPAIGN_STATUS.ACTIVE) {
    return false;
  }

  if (!isCampaignDateEligible(now, campaign, previewMode)) {
    return false;
  }

  if (!isRouteEligible(pathname, surfaceConfig.allowedRoutes, surfaceConfig.allowedRoutePrefixes)) {
    return false;
  }

  if (!isUserEligible(isAuthenticated, campaign.eligibility)) {
    return false;
  }

  const cooldownMs = surfaceConfig.cooldownMs ?? 0;
  const shouldEnforceCooldown = Boolean(surfaceConfig.storageKey)
    && cooldownMs > 0
    && (surfaceConfig.enforceCooldown === true || !previewMode);

  if (shouldEnforceCooldown) {
    if (!isCooldownExpired({
      storageKey: surfaceConfig.storageKey,
      cooldownMs,
      now,
      storage: surfaceConfig.storage,
    })) {
      return false;
    }
  }

  return true;
}
